import type { SiteManifest } from './types';

/**
 * Heartzone Enterprises site-content manifest. Defaults are the copy and
 * artwork of the client's previous website (heartzone.store) VERBATIM, so an
 * un-edited site renders the same as the one their customers already know.
 *
 * Most of that site's visual content is ARTWORK with the words baked into the
 * picture (hero slides, feature band, sign-up banner, certifications, the About
 * story). Each one is an image field here, so the client swaps a picture rather
 * than editing text; the `alt` fields carry the same words for screen readers
 * and search engines.
 *
 * Group ids are `hz-*` because the manifest lint forbids any field path that
 * another shipped manifest already claims (AGP owns `home.hero.*`).
 *
 * NOT here (already runtime-editable elsewhere): the social links (storefront
 * `social`), brand names (`brand`), nav labels/routes and colors/fonts.
 */
export const HEARTZONE_SITE: SiteManifest = {
  tenant: 'heartzone',
  label: 'Heartzone Website',
  // The home page is bespoke, but /storefront is the shared marketplace, so the
  // Home tab (its hero slides/banners) and Featured Packages still render there.
  // Official Store and Landing Packages never do.
  adminHide: {
    storefrontTabs: ['official'],
    sidebarItems: ['/landing-packages'],
  },
  groups: [
    {
      id: 'hz-home',
      label: 'Home',
      sections: [
        {
          id: 'hero',
          label: 'Hero carousel',
          hint: 'Wide banners, 1920 × 757 px. The words are part of the picture.',
          fields: [
            {
              id: 'slides',
              kind: 'objectList',
              label: 'Slides',
              itemFields: [
                { id: 'imageKey', kind: 'image', label: 'Banner' },
                { id: 'alt', kind: 'text', label: 'What the banner says' },
              ],
              default: [
                {
                  imageKey: '/tenants/heartzone/hero-1.webp',
                  alt: 'Heartzone Enterprises, Inc. Enjoying life without compromising your health.',
                },
                {
                  imageKey: '/tenants/heartzone/hero-2.webp',
                  alt: 'Embrace wellness with our healthy products.',
                },
              ],
              maxItems: 6,
            },
          ],
        },
        {
          id: 'features',
          label: 'Feature band',
          hint: 'Shown on computers only, under the carousel.',
          fields: [
            {
              id: 'image',
              kind: 'image',
              label: 'Band artwork',
              defaultUrl: '/tenants/heartzone/features.webp',
            },
            {
              id: 'alt',
              kind: 'textarea',
              label: 'What the band says',
              default:
                'Healthy products: provides quality and healthy products. Value for money: giving you the quality products at the best price. 100% natural: we guarantee to give you the best and natural products. Fast delivery: deliver at your doorsteps.',
            },
          ],
        },
        {
          id: 'products',
          label: 'Products',
          fields: [
            {
              id: 'title',
              kind: 'text',
              label: 'Heading (computers)',
              default: 'Heartzone Quality Products',
            },
            {
              id: 'mobileTitle',
              kind: 'text',
              label: 'Heading (phones)',
              default: 'Featured Products',
            },
            { id: 'showMore', kind: 'text', label: 'Show more link', default: 'Show more...' },
          ],
        },
        {
          id: 'join',
          label: 'Sign-up banner',
          hint: 'Shown to visitors who are not signed in.',
          fields: [
            {
              id: 'image',
              kind: 'image',
              label: 'Banner',
              defaultUrl: '/tenants/heartzone/join.webp',
            },
            {
              id: 'alt',
              kind: 'text',
              label: 'What the banner says',
              default: 'Sign up and be a member',
            },
            { id: 'cta', kind: 'text', label: 'Button', default: 'Join Now' },
          ],
        },
        {
          id: 'certified',
          label: 'Certifications',
          fields: [
            {
              id: 'image',
              kind: 'image',
              label: 'Artwork',
              defaultUrl: '/tenants/heartzone/certified.webp',
            },
            {
              id: 'alt',
              kind: 'text',
              label: 'What the artwork says',
              default: 'Certified: GS1, IPOPHL, FDA',
            },
          ],
        },
      ],
    },
    {
      id: 'hz-about',
      label: 'About',
      sections: [
        {
          id: 'story',
          label: 'Company story',
          fields: [
            {
              id: 'image',
              kind: 'image',
              label: 'Story artwork',
              defaultUrl: '/tenants/heartzone/about.webp',
            },
            {
              id: 'alt',
              kind: 'textarea',
              label: 'What the artwork says',
              default:
                'Heartzone Enterprises Inc. is a corporation which is organized to carry on the business of importing and exporting, buying and selling all kinds of goods but not limited to herbal products. It was incorporated last July 24, 2017 with the aim to start right and reach consumers, to offer them quality products. The company has the name that reflects its motto of offering products to consumers with joy and sincerity. The company is approved by Food and Drug Administration, and was given a License to Operate. Its products have been tested by FDA approved Laboratory as attested to by its Nutrition Facts. The Vision: "To serve as an instrument to develop and offer high quality products and distribute them to everybody." The Mission: To make people healthy and wealthy through the pleasure and enjoyment of using our products for the Greater Glory of the LORD.',
            },
          ],
        },
        {
          id: 'newsletter',
          label: 'Mailing list',
          hint: 'Sign-ups arrive in the admin inbox as contact inquiries.',
          fields: [
            {
              id: 'title',
              kind: 'text',
              label: 'Title',
              default: 'Sign up for more cool items just for you',
            },
            {
              id: 'sub',
              kind: 'text',
              label: 'Subtitle',
              default: 'Be updated with the latest tech news and get exclusive deals',
            },
            {
              id: 'placeholder',
              kind: 'text',
              label: 'Email box hint',
              default: 'Enter Email Address',
            },
            {
              id: 'sent',
              kind: 'text',
              label: 'Thank-you message',
              default: "Thank you! We'll keep you posted.",
            },
          ],
        },
      ],
    },
    {
      id: 'hz-contact',
      label: 'Contact',
      sections: [
        {
          id: 'form',
          label: 'Message form',
          fields: [
            { id: 'title', kind: 'text', label: 'Page title', default: 'Contact Us' },
            {
              id: 'question',
              kind: 'text',
              label: 'Form heading',
              default: 'have some question? / suggestions?',
            },
            { id: 'button', kind: 'text', label: 'Button', default: 'Send' },
            { id: 'sentTitle', kind: 'text', label: 'Sent heading', default: 'Message sent' },
            {
              id: 'sentBody',
              kind: 'textarea',
              label: 'Sent message',
              default: 'Thank you for reaching out. We will get back to you soon.',
            },
            {
              id: 'image',
              kind: 'image',
              label: 'Picture beside the form',
              defaultUrl: '/tenants/heartzone/mail.webp',
            },
          ],
        },
        {
          id: 'details',
          label: 'Contact details',
          hint: 'Also shown in the footer of every page.',
          fields: [
            { id: 'email', kind: 'text', label: 'Email', default: 'heartzone811@gmail.com' },
            { id: 'phone', kind: 'text', label: 'Phone', default: '0926-636-3253' },
            {
              id: 'address',
              kind: 'textarea',
              label: 'Address',
              default: 'NO. 18 SOUTH AA ST., DON ROCES AVENUE, BRGY. PALIGSAHAN, QUEZON CITY',
            },
            { id: 'hours', kind: 'text', label: 'Office hours', default: '9:00am - 6:00pm' },
          ],
        },
      ],
    },
    {
      id: 'hz-chrome',
      label: 'Header & footer',
      sections: [
        {
          id: 'header',
          label: 'Header',
          fields: [
            {
              id: 'company',
              kind: 'textarea',
              label: 'Company name beside the logo',
              default: 'Heartzone\nEnterprises Inc.',
              hint: 'Line breaks are kept.',
            },
          ],
        },
        {
          id: 'footer',
          label: 'Footer',
          fields: [
            {
              id: 'logo',
              kind: 'image',
              label: 'Footer logo',
              defaultUrl: '/tenants/heartzone/logo-lockup.png',
            },
            { id: 'navTitle', kind: 'text', label: 'Links heading', default: 'Heartzone' },
            {
              id: 'contactTitle',
              kind: 'text',
              label: 'Contact heading',
              default: 'Contact Information',
            },
            { id: 'followTitle', kind: 'text', label: 'Social heading', default: 'Follow Us' },
            {
              id: 'copyright',
              kind: 'text',
              label: 'Copyright line',
              default: '© Heartzone. All Rights Reserved',
            },
          ],
        },
      ],
    },
  ],
};
